import { useEffect, useState } from "react";
import { TrendUp, TrendDown, Minus } from "@phosphor-icons/react";
import { Line, LineChart, ResponsiveContainer, Tooltip } from "recharts";
import { api } from "../lib/api";

const METRICS = [
  { key: "squat_1rm_lb", label: "Squat", unit: "lb", direction: "up" },
  { key: "bench_1rm_lb", label: "Bench", unit: "lb", direction: "up" },
  { key: "deadlift_1rm_lb", label: "Deadlift", unit: "lb", direction: "up" },
  { key: "ohp_1rm_lb", label: "OHP", unit: "lb", direction: "up" },
  { key: "max_pushups", label: "Push-ups", unit: "", direction: "up" },
  { key: "max_pullups", label: "Pull-ups", unit: "", direction: "up" },
  { key: "plank_seconds", label: "Plank", unit: "s", direction: "up" },
  { key: "two_mile_run_seconds", label: "2-Mile Run", unit: "s", direction: "down" },
  { key: "twelve_mile_ruck_seconds", label: "12-Mile Ruck", unit: "s", direction: "down" },
  { key: "body_weight_lb", label: "Body Weight", unit: "lb", direction: null },
];

function fmtValue(metric, v) {
  if (v == null) return "—";
  if (metric.key === "two_mile_run_seconds") {
    const m = Math.floor(v / 60);
    const s = Math.round(v % 60);
    return `${m}:${String(s).padStart(2, "0")}`;
  }
  if (metric.key === "twelve_mile_ruck_seconds") {
    const h = Math.floor(v / 3600);
    const m = Math.floor((v % 3600) / 60);
    const s = Math.round(v % 60);
    return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }
  if (metric.key.includes("1rm")) return `${Math.round(v)}${metric.unit ? " " + metric.unit : ""}`;
  return `${v}${metric.unit ? " " + metric.unit : ""}`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "2-digit" });
  } catch {
    return "—";
  }
}

function deltaInfo(metric, current, baseline) {
  if (current == null || baseline == null) return null;
  const diff = current - baseline;
  if (Math.abs(diff) < 0.01) return { kind: "flat", text: "no change" };
  const improved =
    metric.direction === "up" ? diff > 0 :
    metric.direction === "down" ? diff < 0 :
    null;
  const abs = Math.abs(diff);
  let text;
  if (metric.key === "two_mile_run_seconds") {
    text = `${Math.floor(abs / 60)}:${String(Math.round(abs % 60)).padStart(2, "0")} ${diff < 0 ? "faster" : "slower"}`;
  } else if (metric.key === "twelve_mile_ruck_seconds") {
    const h = Math.floor(abs / 3600);
    const m = Math.floor((abs % 3600) / 60);
    const s = Math.round(abs % 60);
    text = `${h > 0 ? h + ":" : ""}${String(m).padStart(h > 0 ? 2 : 1, "0")}:${String(s).padStart(2, "0")} ${diff < 0 ? "faster" : "slower"}`;
  } else {
    text = `${diff > 0 ? "+" : "−"}${metric.key.includes("1rm") ? abs.toFixed(0) : abs}${metric.unit ? " " + metric.unit : ""}`;
  }
  return { kind: improved === null ? "flat" : improved ? "up" : "down", text };
}

/**
 * 60-px-wide inline sparkline. Plots each snapshot's value on a tiny line
 * so users get a glanceable progress shape next to the numbers. Returns
 * null if fewer than 2 points have data — the empty axes would just be
 * noise. Colors green when the metric improved overall, red when it
 * regressed.
 */
function MetricSparkline({ metric, history }) {
  const points = history
    .map((h, i) => {
      const v = h.snapshot?.[metric.key];
      return v == null ? null : { i, v: Number(v) };
    })
    .filter(Boolean);
  if (points.length < 2) return <div className="text-[10px] text-muted-foreground/40 italic">—</div>;

  const first = points[0].v;
  const last = points[points.length - 1].v;
  const diff = last - first;
  const improved =
    Math.abs(diff) < 0.01 ? null
      : metric.direction === "up" ? diff > 0
      : metric.direction === "down" ? diff < 0
      : null;
  const stroke = improved === null ? "#9ca3af" : improved ? "var(--volt, #B6FF14)" : "#f87171";

  return (
    <div className="w-[80px] h-[28px]" data-testid={`sparkline-${metric.key}`}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={points} margin={{ top: 4, right: 2, bottom: 4, left: 2 }}>
          <Line
            type="monotone"
            dataKey="v"
            stroke={stroke}
            strokeWidth={1.5}
            dot={{ r: 1.5, fill: stroke }}
            isAnimationActive={false}
          />
          <Tooltip
            contentStyle={{ background: "#141517", border: "1px solid #272A2D", padding: "2px 6px", fontSize: 11 }}
            labelStyle={{ display: "none" }}
            formatter={(val) => [fmtValue(metric, val), metric.label]}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}


/**
 * Per-snapshot improvement table. Rows = metrics; columns = each saved
 * assessment with its date. Final column shows the overall change from the
 * first snapshot to the current one.
 *
 * `mode='self'` fetches history from /me/assessment/history.
 * `mode='member'` expects the parent to pass `history` directly.
 */
export default function AssessmentHistory({ mode = "self", history: historyProp = null, title = "// IMPROVEMENT" }) {
  const [fetched, setFetched] = useState(null);
  const [err, setErr] = useState("");
  const history = mode === "self" ? fetched : historyProp;

  useEffect(() => {
    if (mode !== "self") return undefined;
    let alive = true;
    api.get("/me/assessment/history")
      .then((r) => { if (alive) setFetched(r.data || []); })
      .catch((e) => { if (alive) setErr(e.response?.data?.detail || e.message); });
    return () => { alive = false; };
  }, [mode]);

  if (err) return <div className="text-red-400 text-sm" data-testid="assessment-history-error">{err}</div>;
  if (!history) return <div className="label-mono animate-pulse" data-testid="assessment-history-loading">LOADING HISTORY...</div>;
  if (history.length < 2) {
    return (
      <div className="border border-dashed border-border p-6 text-sm text-muted-foreground" data-testid="assessment-history-empty">
        Save at least two assessments to start tracking your progress. The next
        time you update, your previous snapshot will be archived here.
      </div>
    );
  }

  const first = history[0];
  const last = history[history.length - 1];

  // Only render metrics that exist in at least one snapshot to keep the
  // table compact for new users.
  const populatedMetrics = METRICS.filter((m) =>
    history.some((h) => h.snapshot?.[m.key] != null),
  );

  return (
    <div data-testid="assessment-history">
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-3">
        <div className="label-mono text-volt">{title}</div>
        <div className="text-[10px] text-muted-foreground uppercase tracking-widest">
          {history.length} snapshot{history.length === 1 ? "" : "s"} · {fmtDate(first.recorded_at)} → {fmtDate(last.recorded_at)}
        </div>
      </div>

      {/* Per-snapshot grid. Each saved assessment is a column, with its date in
          the header. Mobile users can scroll horizontally if many snapshots. */}
      <div className="overflow-x-auto border border-border">
        <table className="w-full text-sm" data-testid="history-table">
          <thead>
            <tr className="bg-secondary">
              <th className="text-left px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground sticky left-0 bg-secondary z-10">Metric</th>
              {history.map((h, i) => (
                <th
                  key={i}
                  className="text-right px-3 py-2 text-[10px] uppercase tracking-widest"
                  data-testid={`history-col-${i}`}
                >
                  <div className={h.is_current ? "text-volt" : "text-muted-foreground"}>{fmtDate(h.recorded_at)}</div>
                  <div className="text-[9px] text-muted-foreground/70 font-normal">
                    {i === 0 ? "baseline" : h.is_current ? "current" : ""}
                  </div>
                </th>
              ))}
              <th className="text-right px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground">Trend</th>
              <th className="text-right px-3 py-2 text-[10px] uppercase tracking-widest text-muted-foreground">Change</th>
            </tr>
          </thead>
          <tbody>
            {populatedMetrics.map((m) => {
              const baseline = first.snapshot?.[m.key];
              const current = last.snapshot?.[m.key];
              const delta = deltaInfo(m, current, baseline);
              const cls = delta?.kind === "up" ? "text-volt" : delta?.kind === "down" ? "text-red-400" : "text-muted-foreground";
              const Icon = delta?.kind === "up" ? TrendUp : delta?.kind === "down" ? TrendDown : Minus;
              return (
                <tr key={m.key} className="border-t border-border" data-testid={`history-row-${m.key}`}>
                  <td className="px-3 py-2 font-display font-bold uppercase sticky left-0 bg-background z-10">{m.label}</td>
                  {history.map((h, i) => {
                    const v = h.snapshot?.[m.key];
                    return (
                      <td
                        key={i}
                        className={`px-3 py-2 text-right tabular-nums ${h.is_current ? "font-display font-black" : "text-muted-foreground"}`}
                        data-testid={`history-cell-${m.key}-${i}`}
                      >
                        {fmtValue(m, v)}
                      </td>
                    );
                  })}
                  <td className="px-3 py-2 align-middle">
                    <div className="flex justify-end">
                      <MetricSparkline metric={m} history={history} />
                    </div>
                  </td>
                  <td className={`px-3 py-2 text-right tabular-nums ${cls}`}>
                    {delta ? (
                      <span className="inline-flex items-center gap-1 justify-end">
                        <Icon size={12} weight="bold" /> {delta.text}
                      </span>
                    ) : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Optional "notes" trail — useful context the user wrote at each
          snapshot (injury notes, etc.). Only shown if anyone had notes. */}
      {history.some((h) => h.snapshot?.notes) && (
        <div className="mt-4 space-y-2" data-testid="history-notes-trail">
          <div className="label-mono text-[10px] text-muted-foreground">// NOTES TIMELINE</div>
          {history.filter((h) => h.snapshot?.notes).map((h, i) => (
            <div key={i} className="border-l-2 border-volt/40 pl-3 py-1">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">{fmtDate(h.recorded_at)}</div>
              <div className="text-sm whitespace-pre-wrap">{h.snapshot.notes}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
